import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, UseFetchEnabledParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

const BASE_URL = `${urlBackend}api/v1/recepcion-equipos`;

export interface EquipoItemRequest {
  id_detalle_contrato?: string | null;
  modelo: string;
  marca?: string | null;
  serie: string;
  direccion_mac?: string | null;
  estado_fisico: string;
}

export interface EvidenciaRequest {
  nombre_original: string;
  base64: string;
  content_type: string;
}

export interface RecepcionEquipoRequest {
  id_contrato: string;
  tipo_incidencia_id: number;
  motivo_retiro_id: number;
  responsable: string;
  fecha_recepcion: string;
  detalle_tecnico?: string | null;
  tipo_servicio?: number;
  id_plan_internet?: number;
  equipos: EquipoItemRequest[];
  evidencias: EvidenciaRequest[];
  cobros?: CobroItemRequest[];
  excepciones?: ExcepcionRequest[];
}

export interface CobroItemRequest {
  concepto: string,
  cantidad: number,
  valor_unitario: number,
  subtotal: number,
  iva: number,
  total: number,
  serie?: string,
}

export interface CobroRequest {
  items: CobroItemRequest[];
}

export interface ExcepcionRequest {
  concepto: string;
  cantidad: number;
  motivo: string;
  descripcion_motivo?: string | null;
  detalle: string;
  archivos?: EvidenciaRequest[];
}

export interface RecepcionEquipoResponse {
  id: string;
  id_contrato: string;
  tipo_incidencia?: { id: number; nombre: string };
  motivo_retiro?: { id: number; nombre: string };
  detalle_tecnico?: string | null;
  responsable: string;
  fecha_recepcion: string;
  estado: string;
  usuario_crea_id?: number;
  fecha_creacion?: string;
  fecha_modificacion?: string;
  items?: EquipoItemResponse[];
  evidencias?: EvidenciaResponse[];
  cobros?: CobroResponse[];
  excepciones?: ExcepcionResponse[];
}

export interface EquipoItemResponse {
  id: string;
  id_detalle_contrato?: string | null;
  modelo: string;
  marca?: string | null;
  serie: string;
  direccion_mac?: string | null;
  estado_fisico: string;
}

export interface EvidenciaResponse {
  id: string;
  nombre_original: string;
  ruta_s3: string;
  tamano_bytes: number;
  content_type: string;
  fecha_subida: string;
}

export interface CobroResponse {
  id: string,
  concepto: string,
  cantidad: number,
  valor_unitario: number,
  subtotal: number,
  iva: number,
  total: number,
  serie?: string,
}

export interface ExcepcionResponse {
  id: string;
  concepto?: string | null;
  cantidad?: number | null;
  motivo: string;
  descripcion_motivo?: string | null;
  detalle: string;
  archivos_json?: string | null;
}

export interface TipoIncidenciaItem {
  id: number;
  nombre: string;
  descripcion?: string | null;
  activo?: boolean;
  fecha_creacion?: string;
}

export interface MotivoRetiroItem {
  id: number;
  tipo_incidencia_id: number;
  tipo_incidencia_nombre?: string;
  nombre: string;
  descripcion?: string | null;
  activo?: boolean;
  fecha_creacion?: string;
}

export interface CostoEquipoItem {
  id: number;
  item: string;
  valor_total: number;
  grupo?: string;
  activo?: boolean;
}

export interface MotivoExcepcionItem {
  id: number;
  nombre: string;
  descripcion?: string | null;
  activo?: boolean;
  fecha_creacion?: string;
}

export interface EquipoClienteDTO {
  id_detalle_contrato?: string | null;
  id_contrato?: string | null;
  cantidad?: string | null;
  serie_mac?: string | null;
  marca?: string | null;
  modelo?: string | null;
  observaciones?: string | null;
  equipo?: string | null;
  detalle_compra?: string | null;
  serie_num?: string | null;
  fecha_sistema?: string | null;
  id_ticket?: string | null;
}

export const useObtenerEquiposCliente = ({
  enabled = true,
  params: { clienteId, contratoId } = { clienteId: '' },
}: UseFetchEnabledParams<{ clienteId: string; contratoId?: string }>) => {
  const queryStr = contratoId ? `?contratoId=${contratoId}` : '';
  return useQuery({
    queryKey: ['recepcion-equipos', 'equipos-cliente', clienteId, contratoId],
    queryFn: () => get<EquipoClienteDTO[]>(`${BASE_URL}/clientes/${clienteId}/equipos${queryStr}`, true),
    enabled: enabled && !!clienteId,
  });
};

export const useFetchRecepcionesByContrato = (idContrato: string) => {
  return useQuery({
    queryKey: ['recepcion-equipos', 'historial', idContrato],
    queryFn: () => get<RecepcionEquipoResponse[]>(`${BASE_URL}/contrato/${idContrato}`, true),
    enabled: !!idContrato,
  });
};

export const useCrearRecepcionEquipo = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: (payload: RecepcionEquipoRequest) =>
      post<RecepcionEquipoResponse>(`${BASE_URL}/`, payload, true),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos', 'historial', variables.id_contrato] });
      setAlert(true, 'Retiro registrado exitosamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useRegistrarCobro = () => {
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CobroRequest }) =>
      post<string>(`${BASE_URL}/${id}/cobro`, payload, true),
    onSuccess: () => {
      setAlert(true, 'Cobro registrado correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useRegistrarExcepcion = () => {
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ExcepcionRequest }) =>
      post<string>(`${BASE_URL}/${id}/excepcion`, payload, true),
    onSuccess: () => {
      setAlert(true, 'Excepción registrada correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useFinalizarRetiro = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: ({ id, idContrato }: { id: string; idContrato: string }) =>
      post<unknown>(`${BASE_URL}/${id}/finalizar`, { observaciones: 'Finalizado', id_contrato: idContrato }, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vision-360'] });
      setAlert(true, 'Retiro finalizado correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// --- Catálogos públicos (para dropdowns en Vision360Panel) ---

export const useFetchTiposIncidencia = () => {
  return useQuery({
    queryKey: ['recepcion-equipos', 'tipos-incidencia'],
    queryFn: () => get<TipoIncidenciaItem[]>(`${BASE_URL}/public/tipos-incidencia`, true),
    refetchOnMount: true,
  });
};

export const useFetchMotivosRetiro = (tipoIncidenciaId: number | null) => {
  return useQuery({
    queryKey: ['recepcion-equipos', 'motivos-retiro', tipoIncidenciaId],
    queryFn: () => get<MotivoRetiroItem[]>(`${BASE_URL}/public/motivos-retiro/${tipoIncidenciaId}`, true),
    enabled: !!tipoIncidenciaId,
    refetchOnMount: true,
  });
};

export const useFetchCostosEquipo = () => {
  return useQuery({
    queryKey: ['recepcion-equipos', 'costos-equipo'],
    queryFn: () => get<CostoEquipoItem[]>(`${BASE_URL}/public/costos-equipo`, true),
    refetchOnMount: true,
  });
};

export const useFetchMotivosExcepcion = () => {
  return useQuery({
    queryKey: ['recepcion-equipos', 'motivos-excepcion'],
    queryFn: () => get<MotivoExcepcionItem[]>(`${BASE_URL}/public/excepciones-motivos`, true),
    refetchOnMount: true,
  });
};

// --- Admin CRUD ---

export const useFetchAdminTiposIncidencia = () => {
  return useQuery({
    queryKey: ['recepcion-equipos', 'admin', 'tipos-incidencia'],
    queryFn: () => get<TipoIncidenciaItem[]>(`${BASE_URL}/admin/tipos-incidencia`, true),
    refetchOnMount: true,
  });
};

export const useCrearTipoIncidencia = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: (data: { nombre: string; descripcion?: string }) =>
      post<TipoIncidenciaItem>(`${BASE_URL}/admin/tipos-incidencia`, data, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Tipo de incidencia creado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useActualizarTipoIncidencia = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { nombre: string; descripcion?: string } }) =>
      put<TipoIncidenciaItem>(`${BASE_URL}/admin/tipos-incidencia/${id}`, data, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Tipo de incidencia actualizado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useToggleTipoIncidencia = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: (id: number) =>
      remove<TipoIncidenciaItem>(`${BASE_URL}/admin/tipos-incidencia/${id}`, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Estado cambiado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useFetchAdminMotivosRetiro = () => {
  return useQuery({
    queryKey: ['recepcion-equipos', 'admin', 'motivos-retiro'],
    queryFn: () => get<MotivoRetiroItem[]>(`${BASE_URL}/admin/motivos-retiro`, true),
    refetchOnMount: true,
  });
};

export const useCrearMotivoRetiro = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: (data: { tipo_incidencia_id: number; nombre: string; descripcion?: string }) =>
      post<MotivoRetiroItem>(`${BASE_URL}/admin/motivos-retiro`, data, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Motivo de retiro creado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useActualizarMotivoRetiro = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { tipo_incidencia_id: number; nombre: string; descripcion?: string } }) =>
      put<MotivoRetiroItem>(`${BASE_URL}/admin/motivos-retiro/${id}`, data, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Motivo de retiro actualizado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useToggleMotivoRetiro = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: (id: number) =>
      remove<MotivoRetiroItem>(`${BASE_URL}/admin/motivos-retiro/${id}`, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Estado cambiado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// --- Admin Excepciones Motivos ---

export const useFetchAdminMotivosExcepcion = () => {
  return useQuery({
    queryKey: ['recepcion-equipos', 'admin', 'motivos-excepcion'],
    queryFn: () => get<MotivoExcepcionItem[]>(`${BASE_URL}/admin/excepciones-motivos`, true),
    refetchOnMount: true,
  });
};

export const useCrearMotivoExcepcion = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: (data: { nombre: string; descripcion?: string }) =>
      post<MotivoExcepcionItem>(`${BASE_URL}/admin/excepciones-motivos`, data, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Motivo de excepción creado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useActualizarMotivoExcepcion = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { nombre: string; descripcion?: string } }) =>
      put<MotivoExcepcionItem>(`${BASE_URL}/admin/excepciones-motivos/${id}`, data, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Motivo de excepción actualizado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useToggleMotivoExcepcion = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: (id: number) =>
      remove<MotivoExcepcionItem>(`${BASE_URL}/admin/excepciones-motivos/${id}`, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Estado cambiado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// --- Admin Costos Equipo ---

export const useFetchAdminCostosEquipo = () => {
  return useQuery({
    queryKey: ['recepcion-equipos', 'admin', 'costos-equipo'],
    queryFn: () => get<CostoEquipoItem[]>(`${BASE_URL}/admin/costos-equipo`, true),
    refetchOnMount: true,
  });
};

export const useCrearCostoEquipo = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: (data: { item: string; valor_total: number; grupo?: string }) =>
      post<CostoEquipoItem>(`${BASE_URL}/admin/costos-equipo`, data, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Costo de equipo creado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useActualizarCostoEquipo = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { item: string; valor_total: number; grupo?: string } }) =>
      put<CostoEquipoItem>(`${BASE_URL}/admin/costos-equipo/${id}`, data, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Costo de equipo actualizado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useToggleCostoEquipo = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: (id: number) =>
      remove<CostoEquipoItem>(`${BASE_URL}/admin/costos-equipo/${id}`, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recepcion-equipos'] });
      setAlert(true, 'Estado cambiado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};
